<template>
  <div class="top-wins-game-item">
    <div
      class="top-wins-game-item__background"
      :style="background ? `background-image: url(${topWin?.game?.compressedImage.webp.lg})` : ''"
    />
    <logo-game v-if="topWin?.game" class="top-wins-game-item__game" :item="topWin?.game" />
    <div class="top-wins-game-item__provider">{{ topWin?.game?.manufacturer?.name }}</div>

    <div class="top-wins-game-item__content">
      <div class="top-wins-game-item__game-title">
        {{ topWin?.game?.name }}
      </div>
      <div class="top-wins-game-item__prize">
        {{ Number(topWin?.amount.toFixed(0)).toLocaleString('ru-RU') }} <span>{{ topWin?.currency }}</span>
      </div>

      <div class="top-wins-game-item__user">
        <icon name="user" size="12" color="var(--cas-light-500)" />

        {{ topWin?.user }}
      </div>
    </div>
  </div>
</template>

<script setup>
import LogoGame from 'components/games/LogoGame'
import { defineProps, toRef } from 'vue'

const props = defineProps({
  topWin: {
    type: Object,
    required: true,
  },
  background: {
    type: Boolean,
    default: false,
  },
})

const topWin = toRef(props.topWin)
</script>

<style lang="scss">
.top-wins-game-item {
  .game-card__footer,
  .game_info__categories {
    display: none !important;
  }

  width: 236px;
  padding: 8px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
  position: relative;
  overflow: hidden;

  &__background {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 0;
    filter: blur(10px);
    background-size: cover;
    background-repeat: no-repeat;
    opacity: 0.3;
    background-position: center;
  }

  &__provider {
    display: none;
  }

  &__game {
    width: 220px;
    filter: drop-shadow(0px 0px 10px rgba(0, 0, 0, 0.4));
    border-radius: 2px;
  }

  &__content {
    position: relative;
    z-index: 1;
  }

  &__game-title {
    font-weight: 500;
    font-size: 14px;
    line-height: 18px;
    text-align: center;
    color: var(--cas-light-900);
    margin-top: 8px;
    text-align: center;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &__prize {
    font-weight: 800;
    font-size: 20px;
    line-height: 20px;
    color: #f7b636;
    margin-top: 8px;
    text-align: center;

    > span {
      font-weight: 800;
      font-size: 16px;
      line-height: 16px;
    }
  }

  &__user {
    margin-top: 8px;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    color: var(--cas-light-700);
    display: flex;
    align-items: center;
    gap: 4px;
    justify-content: center;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  &--swiper {
    width: 180px;

    .top-wins-game-item {
      &__game {
        width: 164px;
      }

      &__game-title {
        font-weight: 500;
        font-size: 12px;
        line-height: 16px;
      }

      &__prize {
        margin-top: 4px;
        font-weight: 800;
        font-size: 16px;
        line-height: 16px;

        > span {
          font-weight: 800;
          font-size: 12px;
          line-height: 12px;
        }
      }

      &__user {
        margin-top: 4px;
        font-weight: 400;
        font-size: 12px;
        line-height: 16px;
      }
    }

    @media screen and (min-width: 470px) {
      width: 320px;

      .top-wins-game-item {
        &__game {
          width: 304px;
        }
      }
    }
  }

  &--small {
    width: 164px;
    flex-basis: calc(50% - 4px);
    max-width: calc(50% - 4px);
    flex-grow: 1;

    .top-wins-game-item {
      &__game {
        width: 100%;
      }

      &__game-title {
        font-weight: 500;
        font-size: 12px;
        line-height: 16px;
      }

      &__prize {
        margin-top: 4px;
        font-weight: 800;
        font-size: 16px;
        line-height: 16px;

        > span {
          font-weight: 800;
          font-size: 12px;
          line-height: 12px;
        }
      }

      &__user {
        margin-top: 4px;
        font-weight: 400;
        font-size: 12px;
        line-height: 16px;
      }
    }

    @media screen and (min-width: 768px) {
      flex-basis: calc((100% - 36px) / 4);
      max-width: calc((100% - 36px) / 4);
    }

    @media screen and (min-width: 1024px) {
      flex-basis: calc((100% - 48px) / 5);
      max-width: calc((100% - 48px) / 5);
    }

    @media screen and (min-width: 1512px) {
      flex-basis: calc((100% - 60px) / 6);
      max-width: calc((100% - 60px) / 6);
    }
  }

  &--large {
    width: 236px;

    @media screen and (min-width: 768px) {
      max-width: 50%;
    }

    .top-wins-game-item__game:hover ~ .top-wins-game-item__provider {
      opacity: 0;
    }

    .top-wins-game-item {
      &__provider {
        display: block;
        position: absolute;
        top: 12px;
        right: 12px;
        z-index: 1;
        padding: 2px 8px;
        background: var(--cas-dark-500);
        backdrop-filter: blur(8px);
        border-radius: 4px;
        font-style: normal;
        font-weight: 500;
        font-size: 10px;
        line-height: 16px;
        color: var(--cas-light-900);
        text-transform: uppercase;
        transition: all 0.2s;
      }

      &__game {
        width: 100%;

        .game_info {
          backdrop-filter: blur(5px);
        }
      }

      &__game-title {
        font-weight: 500;
        font-size: 14px;
        line-height: 18px;
      }

      &__prize {
        margin-top: 8px;
        font-weight: 800;
        font-size: 20px;
        line-height: 20px;

        > span {
          font-weight: 800;
          font-size: 16px;
          line-height: 16px;
        }
      }

      &__user {
        margin-top: 8px;
        font-weight: 400;
        font-size: 12px;
        line-height: 16px;
      }
    }

    @media screen and (min-width: 768px) {
      padding: 36px 8px 16px;
      width: 100%;

      .top-wins-game-item__game:hover ~ .top-wins-game-item__provider {
        opacity: 1;
      }

      .top-wins-game-item {
        &__provider {
          top: 8px;
          right: 8px;
        }

        &__game {
        }

        &__game-title {
          font-size: 18px;
          line-height: 24px;
        }

        &__prize {
          font-weight: 800;
          font-size: 24px;
          line-height: 24px;

          > span {
            font-weight: 800;
            font-size: 20px;
            line-height: 20px;
          }
        }

        &__user {
          margin-top: 8px;
          font-weight: 400;
          font-size: 14px;
          line-height: 18px;
        }
      }
    }

    @media screen and (min-width: 1024px) {
      padding: 36px 8px 16px;
      width: 100%;

      .top-wins-game-item {
        &__provider {
          top: 8px;
          right: 8px;
        }

        &__game {
          width: 220px;
          margin: 0 auto;
        }
      }
    }

    @media screen and (min-width: 1512px) {
      padding: 36px 8px 16px;
      width: calc((50% - 18px) / 2);

      &:first-child {
        flex-grow: 1;
      }
    }
  }
}
</style>
